/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  AppWindow,
  ExternalLink,
  Server,
  Settings2,
  ShieldAlert,
  Wand2,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ForgeApplicationWithContext,
  ForgeClient,
  ForgeWithDomain,
  Organization,
} from '@/types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/Common/Card';
import forgeService from '@/services/forgeService';

interface OrganizationForgeProps {
  organization: Organization;
}

const StatusBadge: React.FC<{ status?: string | null }> = ({ status }) => {
  if (!status) return null;
  const lower = status.toLowerCase();
  const cls =
    lower === 'deployed' || lower === 'healthy'
      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      : lower === 'error' || lower === 'build_failed' || lower === 'unhealthy'
      ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
      : lower === 'creating' ||
        lower === 'building' ||
        lower === 'deploying' ||
        lower === 'updating' ||
        lower === 'initializing'
      ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
      : 'bg-muted text-muted-foreground';
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>
      {status}
    </span>
  );
};

const ForgeRow: React.FC<{ forge: ForgeWithDomain; apps: ForgeApplicationWithContext[] }> = ({
  forge,
  apps,
}) => {
  const { t } = useTranslation();
  return (
    <div className="rounded-md border border-border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Wand2 className="h-4 w-4 shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-medium">{forge.display_name || forge.name}</span>
            <StatusBadge status={forge.status} />
          </div>
          <div className="text-xs text-muted-foreground">
            {forge.domain_name
              ? t('organizationForge.linkedDomain', "Domaine lié: {{name}}", {
                  name: forge.domain_name,
                })
              : t('organizationForge.noDomainLink', 'Aucun domaine lié')}
            <span className="ml-2">
              · {t('organizationForge.appsCount', '{{count}} app(s)', { count: apps.length })}
            </span>
          </div>
        </div>
      </div>
      {(forge.system_apps?.length || forge.custom_apps?.length) && (
        <div className="mt-2 flex flex-wrap gap-1">
          {forge.system_apps?.map((s) => (
            <span
              key={`sys-${s}`}
              className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground"
            >
              {s}
            </span>
          ))}
          {forge.custom_apps?.map((c) => (
            <span
              key={`cus-${c}`}
              className="rounded bg-primary/10 px-1.5 py-0.5 text-xs text-primary"
            >
              {c}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export const OrganizationForge: React.FC<OrganizationForgeProps> = ({ organization }) => {
  const { t } = useTranslation();
  const [client, setClient] = useState<ForgeClient | null>(null);
  const [forges, setForges] = useState<ForgeWithDomain[]>([]);
  const [apps, setApps] = useState<ForgeApplicationWithContext[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        if (!organization.forge_client_id) {
          if (!cancelled) {
            setClient(null);
            setForges([]);
            setApps([]);
          }
          return;
        }
        const [c, f, a] = await Promise.all([
          forgeService.getClient(organization.forge_client_id),
          forgeService.listForgesWithDomains(organization.forge_client_id),
          forgeService.listApplicationsWithContext(),
        ]);
        if (cancelled) return;
        setClient(c);
        setForges(f);
        setApps(a.filter((app) => app.client_id === organization.forge_client_id));
      } catch (err: any) {
        if (!cancelled) {
          console.error('Error loading forge details:', err);
          setError(err?.message || t('organizationForge.loadError', 'Erreur de chargement.'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [organization.forge_client_id, t]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Server className="h-5 w-5" />
          {t('organizationForge.title', 'Forge')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {!organization.forge_client_id ? (
          <div className="flex items-start gap-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              {t(
                'organizationForge.notLinked',
                "Cette organisation n'est liée à aucun client Forge. Liez un client dans l'onglet Aperçu."
              )}
            </div>
          </div>
        ) : (
          <>
            <section>
              <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
                <Server className="h-4 w-4" />
                {t('organizationForge.clientTitle', 'Client Forge lié')}
              </div>
              {loading && !client ? (
                <div className="text-sm text-muted-foreground">
                  {t('global.loading', 'Chargement...')}
                </div>
              ) : error ? (
                <div className="text-sm text-destructive">{error}</div>
              ) : client ? (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <div className="text-xs font-medium text-muted-foreground">
                      {t('organizationForge.clientName', 'Nom du client')}
                    </div>
                    <div className="mt-0.5 text-sm font-semibold">{client.name}</div>
                  </div>
                  <div>
                    <div className="text-xs font-medium text-muted-foreground">
                      {t('organizationForge.clientId', 'ID')}
                    </div>
                    <div className="mt-0.5 break-all font-mono text-xs">{client.client_id}</div>
                  </div>
                  <div>
                    <div className="text-xs font-medium text-muted-foreground">
                      {t('organizationForge.forgesCount', 'Forges')}
                    </div>
                    <div className="mt-0.5 text-sm">{client.forge_ids.length}</div>
                  </div>
                  <div>
                    <div className="text-xs font-medium text-muted-foreground">
                      {t('organizationForge.quotas', 'Quotas')}
                    </div>
                    <div className="mt-0.5 text-xs text-muted-foreground">
                      {client.quotas
                        ? t('organizationForge.quotasValue', {
                            cpu: client.quotas.cpu,
                            memory: client.quotas.memory,
                            pods: client.quotas.pods,
                          })
                        : '—'}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">
                  {t('organizationForge.clientNotFound', 'Client Forge introuvable.')}
                </div>
              )}
            </section>

            <section className="border-t border-border pt-6">
              <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
                <Wand2 className="h-4 w-4" />
                {t('organizationForge.forgesTitle', 'Forges')}
                <span className="ml-1 text-xs font-normal text-muted-foreground">
                  ({forges.length})
                </span>
              </div>
              {loading ? (
                <div className="text-sm text-muted-foreground">
                  {t('global.loading', 'Chargement...')}
                </div>
              ) : forges.length === 0 ? (
                <div className="text-sm text-muted-foreground">
                  {t('organizationForge.noForges', 'Aucune forge pour ce client.')}
                </div>
              ) : (
                <div className="space-y-2">
                  {forges.map((f) => (
                    <ForgeRow
                      key={f.forge_id}
                      forge={f}
                      apps={apps.filter((a) => a.forge_id === f.forge_id)}
                    />
                  ))}
                </div>
              )}
            </section>

            <section className="border-t border-border pt-6">
              <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
                <AppWindow className="h-4 w-4" />
                {t('organizationForge.appsTitle', 'Applications')}
                <span className="ml-1 text-xs font-normal text-muted-foreground">
                  ({apps.length})
                </span>
              </div>
              {loading ? (
                <div className="text-sm text-muted-foreground">
                  {t('global.loading', 'Chargement...')}
                </div>
              ) : apps.length === 0 ? (
                <div className="text-sm text-muted-foreground">
                  {t('organizationForge.noApps', 'Aucune application déployée.')}
                </div>
              ) : (
                <div className="space-y-2">
                  {apps.map((a) => (
                    <div
                      key={a.app_id}
                      className="flex flex-wrap items-center gap-2 rounded-md border border-border p-3"
                    >
                      <AppWindow className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="truncate text-sm font-medium">{a.name}</span>
                          <StatusBadge status={a.status} />
                        </div>
                        <div className="text-xs text-muted-foreground">
                          <span className="mr-3">
                            <Settings2 className="mr-1 inline h-3 w-3" />
                            {a.app_type} · {a.template_name}
                          </span>
                          {a.use_case_name && (
                            <span className="mr-3">
                              {t('organizationForge.linkedUseCase', "Cas d'usage")}: {a.use_case_name}
                            </span>
                          )}
                          {a.health_status && (
                            <span className="mr-3">
                              {t('organizationForge.health', 'Santé')}: {a.health_status}
                            </span>
                          )}
                        </div>
                      </div>
                      {a.urls?.frontend && (
                        <a
                          href={a.urls.frontend}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs hover:bg-muted"
                        >
                          <ExternalLink className="h-3 w-3" />
                          {t('organizationForge.openFrontend', 'Frontend')}
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default OrganizationForge;
